
import colors from 'lib/constants/resourceColors';
import Privileges from 'lib/constants/privileges';

// ⚠️ THIS SCREEN IS A BILLING INPUT. Since migration 1786600050 a holiday flagged
// "Open for Business = No" makes that entire calendar day bill as OVERTIME on every work-order
// visit -- Henry's rule (3). Adding, removing or re-flagging a row here changes what the
// company invoices for labour on that day. It is not a wall calendar.
//
// SINCE 1786600052 IT NO LONGER RUNS OUT. A holiday can carry a RECURRENCE RULE and work out
// its own date every year, forever -- Henry, 2026-08-21: "yes let's expand the holiday to
// figure out the dates on its own". Four kinds of row:
//   Repeats: No          a single date. The legacy behaviour, still the default.
//   Fixed date           month + day. Christmas = Dec 25.
//   Nth weekday          Memorial Day = LAST Monday of May, Thanksgiving = 4th Thursday of Nov.
//   Easter-based         Good Friday = Easter - 2 days.
// For a repeating row the DATE FIELD DISAPPEARS -- there is nothing to type, because the rule
// determines it. The Date column shows the next date the rule produces.

const RECURRENCE_OPTIONS = [
    {value: 'manual',      label: 'No - one specific date'},
    {value: 'fixed',       label: 'Yes - same date every year (e.g. Dec 25)'},
    {value: 'nth_weekday', label: 'Yes - nth weekday of a month (e.g. 4th Thursday of November)'},
    {value: 'easter',      label: 'Yes - relative to Easter Sunday (e.g. Good Friday)'},
];

// Every mode except 'manual'. Used by the fields that are common to all repeating rows.
const REPEATING = ['fixed', 'nth_weekday', 'easter'];

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const MONTH_OPTIONS = MONTH_NAMES.map((label, i) => ({value: i + 1, label}));

// ISO weekday numbering, 1 = Monday .. 7 = Sunday, matching EXTRACT(ISODOW) in the database.
const WEEKDAY_NAMES = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const WEEKDAY_OPTIONS = WEEKDAY_NAMES.map((label, i) => ({value: i + 1, label}));

const NTH_OPTIONS = [
    {value: 1,  label: '1st'},
    {value: 2,  label: '2nd'},
    {value: 3,  label: '3rd'},
    {value: 4,  label: '4th'},
    {value: 5,  label: '5th'},
    {value: -1, label: 'Last'},
];

const ordinal = (n) => (NTH_OPTIONS.find(o => o.value === Number(n)) || {}).label || n;
const monthName = (m) => MONTH_NAMES[Number(m) - 1] || '';
const weekdayName = (d) => WEEKDAY_NAMES[Number(d) - 1] || '';

// Plain-English rendering of the rule, for the list column and the read-only summary. Mirrors
// holiday_date_for_year() in 1786600052 -- if you change one, change the other.
const describeRecurrence = (row) => {
    if(!row){ return ''; }
    const off = Number(row.recurrence_offset_days) || 0;
    // The offset is what makes Black Friday exact: it is the day AFTER the 4th Thursday, which
    // is NOT the same as the 4th Friday (they differ whenever Nov 1 falls on a Friday).
    const shift = off === 0 ? ''
                : off > 0  ? ` + ${off} day${off === 1 ? '' : 's'}`
                           : ` - ${Math.abs(off)} day${Math.abs(off) === 1 ? '' : 's'}`;

    let base;
    switch(row.recurrence){
        case 'fixed':
            base = `${monthName(row.recurrence_month)} ${row.recurrence_day}`;
            break;
        case 'nth_weekday':
            base = `${ordinal(row.recurrence_nth)} ${weekdayName(row.recurrence_weekday)} of `
                 + `${monthName(row.recurrence_month)}`;
            break;
        case 'easter':
            base = 'Easter Sunday';
            break;
        default:
            return 'No';
    }

    const range = row.start_year && row.end_year ? ` (${row.start_year}-${row.end_year})`
                : row.start_year                 ? ` (from ${row.start_year})`
                : row.end_year                   ? ` (until ${row.end_year})`
                : '';
    return `${base}${shift}${range}`;
};

export default {
    defaults: {
        name: 'Holiday',
        color: colors.holidays,
        formFields: [
            '!name',
            'description',

            // The driver of every condition below. It MUST be type 'options' and MUST declare
            // an inputRef -- the Form only publishes live values into inputRefs for options
            // fields, and condition.key is matched against inputRefs, not against the field key.
            {key: 'recurrence',
                label: 'Repeats Every Year?',
                type: 'options',
                required: true,
                defaultValue: 'manual',
                inputRef: 'recurrence',
                options: RECURRENCE_OPTIONS},

            // Only a one-off holiday has a date to type. For a repeating row the field is
            // hidden and never submitted; the database trigger fills holiday_date in from the
            // rule. That is the whole point -- nobody keys a date for Thanksgiving again.
            {required: true, type: 'date', key: 'holiday_date', label: 'Date',
                condition: {key: 'recurrence', values: ['manual']}},

            {key: 'recurrence_month',
                label: 'Month',
                type: 'options',
                options: MONTH_OPTIONS,
                condition: {key: 'recurrence', values: ['fixed', 'nth_weekday']}},

            {key: 'recurrence_day',
                label: 'Day of Month',
                type: 'number',
                condition: {key: 'recurrence', values: ['fixed']}},

            {key: 'recurrence_nth',
                label: 'Which Weekday',
                type: 'options',
                options: NTH_OPTIONS,
                condition: {key: 'recurrence', values: ['nth_weekday']}},

            {key: 'recurrence_weekday',
                label: 'Weekday',
                type: 'options',
                options: WEEKDAY_OPTIONS,
                condition: {key: 'recurrence', values: ['nth_weekday']}},

            {key: 'recurrence_offset_days',
                label: 'Shift By (days)',
                type: 'number',
                placeholder: '0',
                condition: {key: 'recurrence', values: REPEATING}},

            {key: 'start_year',
                label: 'First Year',
                type: 'number',
                placeholder: 'Any',
                condition: {key: 'recurrence', values: REPEATING}},

            {key: 'end_year',
                label: 'Last Year',
                type: 'number',
                placeholder: 'Never ends',
                condition: {key: 'recurrence', values: REPEATING}},

            {key: 'business_open', type: 'boolean', defaultValue: true},
        ],
    },

    search: {
        // These become the literal SELECT list sent to /holidays, so every recurrence column
        // the list or the Repeats column needs has to appear here or it arrives undefined.
        fields: ['id', 'name', 'description', 'holiday_date', 'business_open',
                 'recurrence', 'recurrence_month', 'recurrence_day', 'recurrence_weekday',
                 'recurrence_nth', 'recurrence_offset_days', 'start_year', 'end_year'],
        links: ['new',
            // Copies a year of holidays into the following year, skipping any date the target
            // year already covers -- whether that cover comes from a stored row OR from a
            // recurrence rule. Idempotent.
            //
            // ⚠️ SINCE 1786600052 IT COPIES ONE-OFF ("Repeats: No") HOLIDAYS ONLY. Repeating
            // holidays need no copying -- they compute themselves -- and duplicating one would
            // create a hand-entered row on a wrong month/day that shadows the correct derived
            // date. What is genuinely left for this button is the handful of holidays that no
            // rule can express: Yom Kippur, Rosh Hashanah and Hanukkah follow the Hebrew
            // calendar. All three are Open for Business = Yes, so they do not affect billing.
            //
            // Superuser-gated, matching every other admin RPC action in the app:
            // RPC_duplicateHolidays auto-registers but is granted to 0 roles.
            {   title: 'Copy One-Off Holidays to Next Year',
                icon: 'calendar',
                rpc: 'duplicateHolidays',
                requiredPrivileges: [Privileges.ADMIN],
                runningText: 'Copying one-off holidays...',
                confirm: 'Copy this year\'s ONE-OFF holidays into next year? Repeating holidays '
                       + '(Christmas, Thanksgiving, Memorial Day, Good Friday...) are skipped -- '
                       + 'they already work out their own dates. This is for the holidays that '
                       + 'follow the Hebrew calendar (Yom Kippur, Rosh Hashanah, Hanukkah) and '
                       + 'any one-off closures. Dates already covered are left alone.',
                confirmOkText: 'Copy',
                resultContent: (r) => {
                    const lines = [r.message];
                    if(r.review && r.review.length){
                        lines.push('');
                        lines.push('Check these dates:');
                        r.review.forEach(h => lines.push(`  ${h.name}: ${h.was} -> ${h.now}`));
                    }
                    if(r.skipped_leap_day && r.skipped_leap_day.length){
                        lines.push('');
                        r.skipped_leap_day.forEach(h =>
                            lines.push(`Skipped ${h.name} (${h.date}) - ${r.target_year} is not a leap year.`));
                    }
                    return lines.join('\n');
                },
            },
        ],
        tableProps: {
            rowURL: '/holidays/:id',
            columns: [
                {heading: 'Name', accessor: 'name', placeholder: 'None'},
                {heading: 'Date', accessor: 'holiday_date', type: 'date', cellWidth: '130px'},
                // Non-sortable by design: 'recurrence' alone does not order meaningfully, and
                // the text is assembled from six columns.
                {heading: 'Repeats', accessor: 'recurrence', cellWidth: '210px',
                    valueFunc: (row) => describeRecurrence(row)},
                {heading: 'Open for Business', type: 'boolean', accessor: 'business_open', cellWidth: '50px'},
                {heading: 'Description', accessor: 'description'},
            ]
        },
    }
};
